'use client'

import { useEffect, useRef } from 'react'
import { ActionIcon, FocusTrap, Text } from '@mantine/core'
import { useFocusReturn } from '@mantine/hooks'
import { IconX } from '@tabler/icons-react'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import classes from './ComparePharmaciesPanel.module.css'

interface ComparePharmaciesPanelProps {
    opened: boolean
    onClose: () => void
    children: React.ReactNode
}

/**
 * A self-contained bottom sheet (mobile) / non-blocking right-side panel (desktop) for
 * the "Compare pharmacies" drawer — deliberately not built on the shared GMDrawerModal
 * (a thin wrapper around Mantine's Modal/Drawer). That combination's nested internals
 * (root/inner/content/body, each with their own default sizing) made a reliable,
 * cross-browser-verified height/width fix hard to pin down; this owns its entire DOM
 * structure directly instead, so every rule here maps to one visible box with no
 * hidden Mantine-internal layer in between.
 *
 * Rendered in place (not via a portal) with position: fixed — ComparePharmaciesDrawer
 * is a top-level sibling in ProductDetailPageContent, not nested inside anything that
 * clips overflow or establishes its own stacking context, so a portal isn't needed
 * here, and skipping it keeps this within the same DOM subtree as the rest of the app
 * for CSS custom property inheritance (theme variables are scoped to a provider
 * element, not set globally on <body>, so a portal straight to document.body loses
 * access to them).
 */
export function ComparePharmaciesPanel({ opened, onClose, children }: ComparePharmaciesPanelProps) {
    const { isSm } = useBreakpoint()
    const panelRef = useRef<HTMLDivElement>(null)

    // React (18) doesn't recognize `inert` as a JSX attribute and drops it, so it's set
    // imperatively here instead — this is what actually keeps the whole panel out of
    // the tab order and accessibility tree while closed.
    useEffect(() => {
        if (panelRef.current) panelRef.current.inert = !opened
    }, [opened])

    // Restores focus to whatever triggered the panel (typically Browse pharmacies) once
    // it closes, without scrolling it into view — needed since FocusTrap only moves
    // focus in on open, and inert (above) removes the close button — along with
    // everything else inside — from focusability with nowhere else for focus to land.
    useFocusReturn({ opened })

    // Body scroll lock only applies on mobile — the desktop panel is deliberately
    // non-blocking, so the page underneath must stay scrollable while it's open.
    useEffect(() => {
        if (!opened || !isSm) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [opened, isSm])

    useEffect(() => {
        if (!opened) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [opened, onClose])

    return (
        <>
            {isSm && (
                // Matches the rest of the app's drawers/modals: clicking the backdrop is a
                // no-op, not a dismiss — only the close button below closes this panel.
                <div className={classes.overlay} data-opened={opened} />
            )}
            {/* Trapping focus only makes sense for the mobile sheet, which is modal
                (aria-modal={isSm} below) — the desktop sidebar is deliberately
                non-blocking, so tab should still be free to leave it for the rest of
                the page. */}
            <FocusTrap active={opened && isSm} innerRef={panelRef}>
                <div
                    className={`${classes.panel} ${isSm ? classes.bottomSheet : classes.sidebar}`}
                    data-opened={opened}
                    role="dialog"
                    aria-modal={isSm}
                    aria-label="Compare pharmacies"
                >
                    <div className={classes.header}>
                        <div className={classes.headerTitleRow}>
                            <Text fz={24} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-primary)">
                                Compare pharmacies
                            </Text>
                            <ActionIcon variant="outline" color="gray" radius="xl" onClick={onClose} aria-label="Close">
                                <IconX size={16} />
                            </ActionIcon>
                        </div>
                        <Text fz={12} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-tertiary)" lh="1.4">
                            Prices shown are from General Medicine, without insurance.
                            <br />
                            Your insurance price may be lower—we&apos;ll show it after your visit.
                        </Text>
                    </div>
                    <div className={classes.body}>{children}</div>
                </div>
            </FocusTrap>
        </>
    )
}
